import { isSameDay } from "date-fns";

import type { ScheduleDraftScheduleItem } from "@btnet/services-btnet/handlers/models/scheduleDraftScheduleItem";
import type { SchedulePredecessor } from "@btnet/services-btnet/handlers/models/schedulePredecessor";

import { btInfo } from "commonComponents/btWrappers/BTConfirm/BTConfirm";

import {
    type GanttEntity,
    type GanttEntry,
    GanttItemType,
} from "entity/schedule/Gantt/Gantt.api.types";

function arePredecessorsEqual(
    preds1: SchedulePredecessor[] | null | undefined,
    preds2: SchedulePredecessor[] | null | undefined,
): boolean {
    const p1 = preds1 ?? [];
    const p2 = preds2 ?? [];

    if (p1.length !== p2.length) return false;

    const sorted1 = [...p1].sort((a, b) => (a.predecessorId ?? 0) - (b.predecessorId ?? 0));
    const sorted2 = [...p2].sort((a, b) => (a.predecessorId ?? 0) - (b.predecessorId ?? 0));

    return sorted1.every((pred1, index) => {
        const pred2 = sorted2[index];
        return (
            pred1.predecessorId === pred2.predecessorId &&
            pred1.type === pred2.type &&
            (pred1.lag ?? 0) === (pred2.lag ?? 0)
        );
    });
}

function hasItemBeenModified(
    originalItem: ScheduleDraftScheduleItem,
    currentItem: GanttEntry,
): boolean {
    const startDate1 = originalItem.startDate ?? undefined;
    const startDate2 = currentItem.startDate.toDate();
    const endDate1 = originalItem.endDate ?? undefined;
    const endDate2 = currentItem.endDate.toDate();

    const startDatesEqual =
        (!startDate1 && !startDate2) ||
        (startDate1 && startDate2 && isSameDay(startDate1, startDate2));
    const endDatesEqual =
        (!endDate1 && !endDate2) || (endDate1 && endDate2 && isSameDay(endDate1, endDate2));

    if (!startDatesEqual || !endDatesEqual) {
        return true;
    }

    if ((originalItem.duration ?? 0) !== (currentItem.duration ?? 0)) {
        return true;
    }

    const currentPreds =
        currentItem.predecessors?.length > 0
            ? currentItem.predecessors.map((p) => ({
                  predecessorId: p.predecessorId,
                  type: p.predecessorType,
                  lag: p.lag,
              }))
            : null;

    return !arePredecessorsEqual(originalItem.predecessors, currentPreds);
}

export function validateDraft(
    originalSnapshot: ScheduleDraftScheduleItem[],
    liveEntity: GanttEntity,
    draftItems: ScheduleDraftScheduleItem[],
): boolean {
    if (!originalSnapshot || !liveEntity || !draftItems) {
        return true;
    }

    const liveScheduleItems = liveEntity.taskData.data.filter(
        (entry) => entry.itemType === GanttItemType.Schedule,
    );

    const liveItemsMap = new Map(liveScheduleItems.map((item) => [item.id, item]));
    const originalItemsMap = new Map(originalSnapshot.map((item) => [item.id, item]));

    // Check for new items in live schedule (indicates changes)
    for (const liveItem of liveScheduleItems) {
        if (!originalItemsMap.has(liveItem.id)) {
            return false;
        }
    }

    // Check each draft item for validation
    for (const draftItem of draftItems) {
        if (draftItem.id == null) continue;

        const liveItem = liveItemsMap.get(draftItem.id);
        const originalItem = originalItemsMap.get(draftItem.id);

        if (!liveItem) {
            // Item was deleted in live schedule
            return false;
        } else if (originalItem) {
            // Existing item - check if live version was modified
            const liveModified = hasItemBeenModified(originalItem, liveItem);
            if (liveModified) {
                return false;
            }
        }
    }

    return true;
}

export function showDraftOutdatedDialog(onDiscardDraft: () => void): void {
    const content = "The live schedule was updated. Your draft changes will be removed.";

    btInfo({
        title: "Schedule update detected",
        content,
        okText: "Refresh",
        onOk: async () => {
            onDiscardDraft();
        },
        centered: true,
        width: 500,
    });
}

export async function handleDraftValidation(isValid: boolean): Promise<boolean> {
    if (isValid) {
        return false;
    }

    return new Promise<boolean>((resolve) => {
        showDraftOutdatedDialog(() => {
            resolve(true);
        });
    });
}
